import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { CheckCircle, ErrorOutline } from '@material-ui/icons';
import { green } from '@material-ui/core/colors';
import { SurveyService } from '../../services/surveyService';

interface SurveyContext {
  loanId: number;
  recipientName: string;
  recipientType: 'LENDER' | 'BORROWER' | null;
  alreadyResponded: boolean;
  preselectedScore: number | null;
}

type PageStatus = 'loading' | 'invalid' | 'network_error' | 'already_responded' | 'form' | 'submitted';
type FormStep = 'score' | 'comment';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: 660,
    margin: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  card: {
    width: '100%',
    maxWidth: 660,
    margin: 'auto',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },
  content: {
    padding: theme.spacing(5, 6),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 3),
    },
  },
  center: {
    textAlign: 'center',
  },
  question: {
    fontWeight: 500,
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  scoreRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    margin: theme.spacing(0, 0, 1),
  },
  scoreButton: {
    minWidth: 46,
    height: 46,
    padding: 0,
    fontWeight: 600,
    fontSize: '0.95rem',
    borderRadius: 6,
    [theme.breakpoints.down('xs')]: {
      minWidth: 36,
      height: 36,
      fontSize: '0.82rem',
    },
  },
  scoreLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.palette.text.secondary,
    fontSize: '0.72rem',
    marginBottom: theme.spacing(1),
    paddingLeft: 2,
    paddingRight: 2,
  },
  textarea: {
    marginBottom: theme.spacing(3),
  },
  submitButton: {
    display: 'block',
    margin: '0 auto',
    minWidth: 140,
    fontWeight: 600,
  },
  successIcon: {
    fontSize: 60,
    color: green[500],
    marginBottom: theme.spacing(1),
  },
  errorIcon: {
    fontSize: 60,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
  spacer: {
    marginTop: theme.spacing(2),
  },
  submitError: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  readOnlyScore: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '1.4rem',
    fontWeight: 700,
    margin: theme.spacing(2, 0),
  },
}));

const surveyService = new SurveyService();

const SCORES = Array.from({ length: 11 }, (_, i) => i);

function commentQuestion(score: number): string {
  return `Tell us a bit more about why you chose ${score}. What did you like? What can we do better?`;
}

export const Survey = observer(() => {
  const classes = useStyles();
  const { token } = useParams<{ token: string }>();
  const location = useLocation();

  const [pageStatus, setPageStatus] = useState<PageStatus>('loading');
  const [formStep, setFormStep] = useState<FormStep>('score');
  const [context, setContext] = useState<SurveyContext | null>(null);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadContext = async () => {
    setPageStatus('loading');
    const params = new URLSearchParams(location.search);
    const raw = parseInt(params.get('score') || '', 10);
    const urlScore = !isNaN(raw) && raw >= 0 && raw <= 10 ? raw : null;

    try {
      const response = await surveyService.getSurveyContext(token);
      const body = response.data;

      if (body.status !== 'OK') {
        setPageStatus('invalid');
        return;
      }

      const ctx: SurveyContext = body.data;
      setContext(ctx);

      if (ctx.alreadyResponded) {
        setSelectedScore(ctx.preselectedScore);
        setPageStatus('already_responded');
      } else {
        const score = urlScore !== null ? urlScore : ctx.preselectedScore;
        setSelectedScore(score);
        setFormStep(score !== null ? 'comment' : 'score');
        setPageStatus('form');
      }
    } catch {
      setPageStatus('network_error');
    }
  };

  useEffect(() => {
    loadContext();
  }, [token]);

  const handleScoreClick = (score: number) => {
    setSelectedScore(score);
    setFormStep('comment');
  };

  const handleSubmit = async () => {
    if (selectedScore === null) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      await surveyService.submitSurvey(token, selectedScore, comment);
      setPageStatus('submitted');
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderScoreStep = () => (
    <>
      <Typography variant="h6" className={classes.question}>
        How likely are you to recommend us to a friend or colleague?
      </Typography>
      {context && (
        <Typography variant="body2" color="textSecondary" className={classes.center} gutterBottom>
          Thanks, {context.recipientName} — we'd love your feedback on Loan #{context.loanId}.
        </Typography>
      )}
      <Box mt={3}>
        <div className={classes.scoreRow}>
          {SCORES.map(i => (
            <Button
              key={i}
              className={classes.scoreButton}
              variant={selectedScore === i ? 'contained' : 'outlined'}
              color={selectedScore === i ? 'primary' : 'default'}
              onClick={() => handleScoreClick(i)}
              size="small"
            >
              {i}
            </Button>
          ))}
        </div>
        <div className={classes.scoreLabels}>
          <span>Not likely</span>
          <span>Very likely</span>
        </div>
      </Box>
    </>
  );

  const renderCommentStep = () => (
    <>
      <Typography variant="h6" className={classes.question}>
        {selectedScore !== null ? commentQuestion(selectedScore) : 'Tell us more about your experience.'}
      </Typography>
      <TextField
        className={classes.textarea}
        multiline
        rows={4}
        fullWidth
        variant="outlined"
        value={comment}
        onChange={e => setComment(e.target.value)}
        autoFocus
      />
      <Button
        className={classes.submitButton}
        variant="contained"
        color="primary"
        size="large"
        disabled={submitting}
        onClick={handleSubmit}
      >
        {submitting ? 'Submitting...' : 'Submit'}
      </Button>
      {submitError && (
        <Typography className={classes.submitError} variant="body2">
          {submitError}
        </Typography>
      )}
    </>
  );

  return (
    <Grid container justifyContent="center" className={classes.container}>
      <Grid item xs={12}>
        <Card className={classes.card}>
          <CardContent className={classes.content}>
            {pageStatus === 'loading' && (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            )}

            {pageStatus === 'invalid' && (
              <div className={classes.center}>
                <ErrorOutline className={classes.errorIcon} />
                <Typography variant="h6" gutterBottom>
                  This survey link is invalid or has expired.
                </Typography>
                <Typography color="textSecondary">
                  Please contact your loan officer if you need assistance.
                </Typography>
              </div>
            )}

            {pageStatus === 'network_error' && (
              <div className={classes.center}>
                <ErrorOutline className={classes.errorIcon} />
                <Typography variant="h6" gutterBottom>
                  Something went wrong. Please try again.
                </Typography>
                <Box mt={2}>
                  <Button variant="contained" color="primary" onClick={loadContext}>
                    Retry
                  </Button>
                </Box>
              </div>
            )}

            {pageStatus === 'already_responded' && context && (
              <div className={classes.center}>
                <CheckCircle className={classes.successIcon} />
                <Typography variant="h5" gutterBottom>
                  You've already responded
                </Typography>
                <Typography color="textSecondary" paragraph>
                  Thanks, {context.recipientName} — we already received your feedback for Loan #
                  {context.loanId}.
                </Typography>
                {selectedScore !== null && (
                  <>
                    <Typography variant="body2" color="textSecondary">
                      Your submitted rating:
                    </Typography>
                    <div className={classes.readOnlyScore}>{selectedScore}</div>
                  </>
                )}
              </div>
            )}

            {pageStatus === 'form' && (
              <>
                {formStep === 'score' && renderScoreStep()}
                {formStep === 'comment' && renderCommentStep()}
              </>
            )}

            {pageStatus === 'submitted' && (
              <div className={classes.center}>
                <CheckCircle className={classes.successIcon} />
                <Typography variant="h5" gutterBottom>
                  Thank you for your feedback!
                </Typography>
                <Typography color="textSecondary">
                  Your response has been recorded.
                  {context && ` We appreciate your time, ${context.recipientName}.`}
                </Typography>
                <Typography color="textSecondary" className={classes.spacer}>
                  You can now close this window.
                </Typography>
              </div>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
});
